import { Search, X, SlidersHorizontal, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PresetCategory, PresetFilterMetadata } from "@profolio/templates";
import type { ProductTier } from "@profolio/validators";

export interface FilterState {
  category: "all" | PresetCategory;
  search: string;
  purpose: "all" | PresetFilterMetadata["purpose"];
  aesthetic: "all" | PresetFilterMetadata["aesthetic"];
  contentEmphasis: "all" | PresetFilterMetadata["contentEmphasis"][number];
  tier: "all" | ProductTier;
}

interface TemplateFilterBarProps {
  filters: FilterState;
  onFilterChange: (filters: Partial<FilterState>) => void;
  onReset: () => void;
  counts: {
    all: number;
    faculty: number;
    research: number;
    earlyCareer: number;
  };
}

export function TemplateFilterBar({
  filters,
  onFilterChange,
  onReset,
  counts,
}: TemplateFilterBarProps) {
  const hasActiveFilters =
    filters.category !== "all" ||
    filters.search.trim().length > 0 ||
    filters.purpose !== "all" ||
    filters.aesthetic !== "all" ||
    filters.contentEmphasis !== "all" ||
    filters.tier !== "all";

  const categories: { id: "all" | PresetCategory; label: string; count: number }[] = [
    { id: "all", label: "All Templates", count: counts.all },
    { id: "faculty-teaching", label: "Faculty & Teaching", count: counts.faculty },
    { id: "research-publications", label: "Research & Publications", count: counts.research },
    { id: "early-career-phd", label: "Early-Career & PhD", count: counts.earlyCareer },
  ];

  return (
    <div className="flex flex-col gap-4 mb-8">
      {/* ── Top Level Category Navigation Tabs ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <div className="flex flex-wrap items-center gap-1.5" role="tablist">
          {categories.map((cat) => {
            const isActive = filters.category === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onFilterChange({ category: cat.id })}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <span>{cat.label}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                    isActive
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {cat.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Free-text Search Input */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onFilterChange({ search: e.target.value })}
            placeholder="Search templates..."
            className="w-full pl-8.5 pr-8 py-1.5 text-xs rounded-lg border border-border bg-background focus:outline-hidden focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => onFilterChange({ search: "" })}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ── Faceted Filter Selectors Bar ── */}
      <div className="flex flex-wrap items-center gap-2.5 text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground font-medium shrink-0 mr-1">
          <SlidersHorizontal className="h-3.5 w-3.5 text-primary" />
          <span>Filter By:</span>
        </div>

        {/* Purpose Filter */}
        <select
          value={filters.purpose}
          onChange={(e) => onFilterChange({ purpose: e.target.value as any })}
          className="px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-2 focus:ring-primary outline-hidden cursor-pointer"
        >
          <option value="all">Purpose: All</option>
          <option value="faculty-profile">Faculty Profile</option>
          <option value="research-showcase">Research Showcase</option>
          <option value="publication-first">Publication First</option>
          <option value="teaching-focused">Teaching Focused</option>
          <option value="academic-cv">Academic CV</option>
          <option value="lab-group">Lab Group</option>
        </select>

        {/* Aesthetic Filter */}
        <select
          value={filters.aesthetic}
          onChange={(e) => onFilterChange({ aesthetic: e.target.value as any })}
          className="px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-2 focus:ring-primary outline-hidden cursor-pointer"
        >
          <option value="all">Aesthetic: All</option>
          <option value="formal">Formal</option>
          <option value="modern">Modern</option>
          <option value="minimal">Minimal</option>
          <option value="editorial">Editorial</option>
          <option value="dark">Dark</option>
          <option value="technical">Technical</option>
        </select>

        {/* Content Emphasis Filter */}
        <select
          value={filters.contentEmphasis}
          onChange={(e) => onFilterChange({ contentEmphasis: e.target.value as any })}
          className="px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-2 focus:ring-primary outline-hidden cursor-pointer"
        >
          <option value="all">Emphasis: All</option>
          <option value="publications">Publications</option>
          <option value="teaching">Teaching</option>
          <option value="projects">Projects</option>
          <option value="grants">Grants</option>
          <option value="experience">Experience</option>
          <option value="skills">Skills</option>
          <option value="awards">Awards</option>
        </select>

        {/* Tier Filter */}
        <select
          value={filters.tier}
          onChange={(e) => onFilterChange({ tier: e.target.value as any })}
          className="px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-2 focus:ring-primary outline-hidden cursor-pointer"
        >
          <option value="all">Tier: All</option>
          <option value="starter">Starter (Included)</option>
          <option value="pro">Pro</option>
          <option value="premium">Premium</option>
        </select>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-xs h-8 text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset Filters
          </Button>
        )}
      </div>
    </div>
  );
}
